import { canAnywhere, type Actor, type ModuleKey, type Permission } from '@hrms/shared';
import { isModuleOn } from '../moduleRoutes.js';

/**
 * What a Home card or quick action needs before it shows. Both halves must hold:
 *
 *   - the **module** is switched on for the company (every module listed, when several), and
 *   - the **person** holds the **permission** (any of them, when several).
 *
 * So Payroll being on for the company shows its links only to the people whose role carries a payroll
 * permission (HR, Finance, Payroll Admin ...), never to everyone. A card that names neither is always
 * shown. This is a courtesy (the API still decides what is allowed), but it means Home never offers a
 * tile that leads to a refusal.
 */
export interface HomeAccessRule {
  module?: ModuleKey | readonly ModuleKey[];
  permission?: Permission | readonly Permission[];
}

const asList = <T>(value: T | readonly T[] | undefined): readonly T[] =>
  value === undefined ? [] : Array.isArray(value) ? (value as readonly T[]) : [value as T];

export function homeAccess(
  actor: Actor,
  enabledModules: readonly ModuleKey[] | undefined,
  rule: HomeAccessRule,
): boolean {
  const modules = asList(rule.module);
  if (!modules.every((module) => isModuleOn(enabledModules, module))) return false;
  const permissions = asList(rule.permission);
  if (permissions.length === 0) return true;
  return permissions.some((permission) => canAnywhere(actor, permission));
}
